import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from 'context/auth/auth_context';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios-instance';
import './index.css';

interface MyPageData {
  userId: number;
  email: string;
  name: string;
  nickname: string;
  createdAt: string;
  authProvider: string;
  communities: Community[];
  posts: Post[];
  replies: Reply[];
  reviews: Review[];
}

interface Community {
  communityUuid: string;
  communityName: string;
  type: string;
  joinedAt: string;
  joinedNickname: string;
}

interface Post {
  postId: number;
  title: string;
  content: string;
  communityUuid: string;
  communityName: string;
  createdAt: string;
  likeCount: number;
  viewCount: number;
  replyCount: number;
}

interface Reply {
  replyId: number;
  postId: number;
  postTitle: string;
  content: string;
  communityUuid: string;
  communityName: string;
  createdAt: string;
  isReply: boolean;
}

interface Review {
  reviewId: number;
  type: string;
  targetId: string;
  content: string;
  createdAt: string;
  likeCount: number;
}

const MyPage: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;
  const navigate = useNavigate();
  const [myPageData, setMyPageData] = useState<MyPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'communities' | 'posts' | 'replies' | 'reviews'>('communities');
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMyPageData();
    }
  }, [isAuthenticated]);

  const fetchMyPageData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/mypage');
      setMyPageData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '마이페이지 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateNickname = async (nickname: string) => {
    try {
      setIsUpdating(true);
      setNicknameError(null);
      
      // 닉네임 유효성 검사
      if (nickname.length < 2 || nickname.length > 20) {
        setNicknameError('닉네임은 2자 이상 20자 이하여야 합니다.');
        return;
      }

      await axiosInstance.put('/api/mypage/nickname', { nickname });
      await fetchMyPageData(); // 데이터 새로고침
      setShowNicknameModal(false);
      setNewNickname('');
    } catch (err: any) {
      setNicknameError(err.response?.data?.message || '닉네임 수정에 실패했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNicknameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newNickname.trim() && newNickname !== myPageData?.nickname) {
      updateNickname(newNickname.trim());
    }
  };

  const openNicknameModal = () => {
    setNewNickname(myPageData?.nickname || '');
    setNicknameError(null);
    setShowNicknameModal(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="mypage-container">
        <div className="mypage-error">
          <h2>로그인이 필요합니다</h2>
          <p>마이페이지를 이용하려면 로그인해주세요.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mypage-container">
        <div className="mypage-loading">
          <h2>로딩 중...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mypage-container">
        <div className="mypage-error">
          <h2>오류가 발생했습니다</h2>
          <p>{error}</p>
          <button onClick={fetchMyPageData} className="retry-button">
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  if (!myPageData) {
    return (
      <div className="mypage-container">
        <div className="mypage-error">
          <h2>데이터를 불러올 수 없습니다</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="mypage-container">
      <div className="mypage-header">
        <h1>마이페이지</h1>
      </div>

      <div className="mypage-content">
        {/* 사용자 정보 섹션 */}
        <div className="user-info-section">
          <div className="user-profile">
            <div className="profile-avatar">
              <span>{myPageData.nickname.charAt(0).toUpperCase()}</span>
            </div>
            <div className="profile-info">
              <h2>{myPageData.nickname}</h2>
              <p className="user-email">{myPageData.email}</p>
              <p className="user-name">{myPageData.name}</p>
              <p className="join-date">
                가입일: {new Date(myPageData.createdAt).toLocaleDateString('ko-KR')}
              </p>
              <p className="auth-provider">
                가입 플랫폼: {myPageData.authProvider}
              </p>
            </div>
            <div className="profile-actions">
              <button 
                className="edit-nickname-btn"
                onClick={openNicknameModal}
              >
                닉네임 수정
              </button>
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'communities' ? 'active' : ''}`}
            onClick={() => setActiveTab('communities')}
          >
            가입한 커뮤니티 ({myPageData.communities.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            작성한 글 ({myPageData.posts.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'replies' ? 'active' : ''}`}
            onClick={() => setActiveTab('replies')}
          >
            작성한 댓글 ({myPageData.replies.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            작성한 리뷰 ({myPageData.reviews.length})
          </button>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="tab-content">
          {activeTab === 'communities' && (
            <div className="communities-section">
              <h3>가입한 커뮤니티</h3>
              {myPageData.communities.length === 0 ? (
                <p className="empty-message">가입한 커뮤니티가 없습니다.</p>
              ) : (
                <div className="communities-list">
                  {myPageData.communities.map((community) => (
                    <div 
                      key={community.communityUuid} 
                      className="community-item clickable"
                      onClick={() => {
                        const path = community.type === 'building' 
                          ? `/community/building/${community.communityUuid}`
                          : `/community/town?uuid=${community.communityUuid}`;
                        navigate(path);
                      }}
                    >
                      <div className="community-info">
                        <h4>{community.communityName}</h4>
                        <p className="community-type">
                          {community.type === 'building' ? '건물 커뮤니티' : '동네 커뮤니티'}
                        </p>
                        <p className="join-date">
                          가입일: {new Date(community.joinedAt).toLocaleDateString('ko-KR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'posts' && (
            <div className="posts-section">
              <h3>작성한 글</h3>
              {myPageData.posts.length === 0 ? (
                <p className="empty-message">작성한 글이 없습니다.</p>
              ) : (
                <div className="posts-list">
                  {myPageData.posts.map((post) => (
                    <div 
                      key={post.postId} 
                      className="post-item clickable"
                      onClick={() => {
                        // communityUuid에서 타입 추출 (building 또는 town)
                        const path = post.communityUuid.startsWith('building')
                          ? `/community/building/${post.communityUuid}/post/${post.postId}`
                          : `/community/town/${post.communityUuid}/post/${post.postId}`;
                        navigate(path);
                      }}
                    >
                      <div className="post-info">
                        <h4>{post.title}</h4>
                        <p className="post-community">{post.communityName}</p>
                        <div className="post-meta">
                          <span>좋아요 {post.likeCount}</span>
                          <span>조회 {post.viewCount}</span>
                          <span>댓글 {post.replyCount}</span>
                          <span>{new Date(post.createdAt).toLocaleDateString('ko-KR')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'replies' && (
            <div className="replies-section">
              <h3>작성한 댓글</h3>
              {myPageData.replies.length === 0 ? (
                <p className="empty-message">작성한 댓글이 없습니다.</p>
              ) : (
                <div className="replies-list">
                  {myPageData.replies.map((reply) => (
                    <div 
                      key={reply.replyId} 
                      className="reply-item clickable"
                      onClick={() => {
                        const path = reply.communityUuid.startsWith('building')
                          ? `/community/building/${reply.communityUuid}/post/${reply.postId}`
                          : `/community/town/${reply.communityUuid}/post/${reply.postId}`;
                        navigate(path);
                      }}
                    >
                      <div className="reply-info">
                        <h4>{reply.postTitle}</h4>
                        <p className="reply-content">{reply.content}</p>
                        <p className="reply-community">{reply.communityName}</p>
                        <div className="reply-meta">
                          <span>{reply.isReply ? '대댓글' : '댓글'}</span>
                          <span>{new Date(reply.createdAt).toLocaleDateString('ko-KR')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-section">
              <h3>작성한 리뷰</h3>
              {myPageData.reviews.length === 0 ? (
                <p className="empty-message">작성한 리뷰가 없습니다.</p>
              ) : (
                <div className="reviews-list">
                  {myPageData.reviews.map((review) => (
                    <div key={review.reviewId} className="review-item">
                      <div className="review-info">
                        <h4>
                          {review.type === 'building' ? '건물 리뷰' : '동네 리뷰'} 
                          (ID: {review.targetId})
                        </h4>
                        <p className="review-content">{review.content}</p>
                        <div className="review-meta">
                          <span>좋아요 {review.likeCount}</span>
                          <span>{new Date(review.createdAt).toLocaleDateString('ko-KR')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 닉네임 수정 모달 */}
      {showNicknameModal && (
        <div className="modal-overlay" onClick={() => setShowNicknameModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>닉네임 수정</h3>
              <button 
                className="modal-close"
                onClick={() => setShowNicknameModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleNicknameSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="nickname">새로운 닉네임</label>
                <input
                  type="text"
                  id="nickname"
                  value={newNickname}
                  onChange={(e) => setNewNickname(e.target.value)}
                  placeholder="닉네임을 입력하세요 (2-20자)"
                  maxLength={20}
                  disabled={isUpdating}
                  className={nicknameError ? 'error' : ''}
                />
                {nicknameError && (
                  <div className="error-message">{nicknameError}</div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowNicknameModal(false)}
                  disabled={isUpdating}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isUpdating || !newNickname.trim() || newNickname === myPageData?.nickname}
                >
                  {isUpdating ? '수정 중...' : '수정'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPage;
